import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { instance as axiosInstance } from '../../../services/axiosInstance';
import AnalyticsService from '../../../services/analytics/AnalyticsService';
import Header from '../../../components/Header';
import { Main, MainContainer } from '../../operational/request/resume/RequestResume';
import { BREADCRUMB_CADASTRO } from '../../../utils/breadcrumbCadastro';
import { MOCK_RULES, MOCK_METADATA } from './mockData';

class RuleList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rules: [],
      draggedIndex: null,
      loading: true
    };
  }

  componentDidMount() {
    this.fetchRules();
  }

  trackDashboardAccess = (totalRulesCount) => {
    AnalyticsService.capture('customization_dashboard_accessed', {
      has_existing_rules: totalRulesCount > 0,
      total_rules_count: totalRulesCount
    });
  }

  fetchRules = async () => {
    try {
      const URL = '/api/administrador/regras-laudo';
      const token = this.props.login && this.props.login.user && this.props.login.user.token 
        ? this.props.login.user.token.access_token 
        : null;
      
      if (!token) {
        console.error('Token não encontrado');
        toastr.error('Erro', 'Token de autenticação não encontrado.');
        this.setState({ rules: [], loading: false });
        return;
      }
      
      const headers = { 'Authorization': `Bearer ${token}` };
      const result = await axiosInstance.get(URL, { headers });
      
      if (result.status === 401) {
        this.props.history.push('/auth');
        return;
      }
      
      // A resposta pode vir como result.data ou result.data.data dependendo do axiosInstance
      const apiData = Array.isArray(result.data) ? result.data : (result.data.data || []);
      
      // Se a API retornar array vazio, é um sucesso (sem regras cadastradas ainda)
      if (apiData.length === 0) {
        this.trackDashboardAccess(0);
        this.setState({ rules: [], loading: false });
        return;
      }
      
      // Mapear resposta da API para o formato interno
      const rules = apiData.map(rule => ({
        id: rule.id,
        name: rule.nome,
        tenant_recommendation_text: rule.texto_parecer_inquilinos || '',
        guarantor_recommendation_text: rule.texto_parecer_fiadores || '',
        is_active: rule.ativa,
        priority: rule.prioridade,
        conditions: rule.condicoes || []
      }));
      
      // Ordenar por prioridade
      rules.sort((a, b) => a.priority - b.priority);

      this.trackDashboardAccess(rules.length);
      
      this.setState({ rules, loading: false });
    } catch (error) {
      console.error('Erro ao buscar regras:', error);
      toastr.error('Erro', 'Erro ao carregar regras da API.');
      this.setState({ rules: [], loading: false });
    }
  }

  handleDragStart = (index) => {
    this.setState({ draggedIndex: index });
  }

  handleDragOver = (e, index) => {
    e.preventDefault();
    
    const { draggedIndex, rules } = this.state;
    
    if (draggedIndex === null || draggedIndex === index) return;

    const newRules = [...rules];
    const draggedItem = newRules[draggedIndex];
    
    newRules.splice(draggedIndex, 1);
    newRules.splice(index, 0, draggedItem);
    
    newRules.forEach((rule, idx) => {
      rule.priority = idx;
    });
    
    this.setState({ 
      rules: newRules,
      draggedIndex: index 
    });
  }

  handleDragEnd = async () => {
    this.setState({ draggedIndex: null });
    
    const { rules } = this.state;
    
    // Atualizar prioridades no backend
    try {
      const URL = '/api/administrador/regras-laudo/reordenar';
      const token = this.props.login.user.token.access_token;
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const data = rules.map(r => r.id);
      
      const result = await axiosInstance.put(URL, { ids: data }, { headers });
      
      if (result.status === 401) {
        this.props.history.push('/auth');
        return;
      }
      toastr.success('Sucesso', 'Ordem das regras atualizada!');
    } catch (error) {
      console.error('Erro ao reordenar regras:', error);
      toastr.error('Erro', 'Erro ao salvar ordem das regras.');
    }
  }

  handleToggleActive = async (id) => {
    const rule = this.state.rules.find(r => r.id === id);
    if (!rule) return;
    
    const newActiveStatus = !rule.is_active;
    
    // Atualizar estado local otimisticamente
    this.setState(prevState => ({
      rules: prevState.rules.map(rule => 
        rule.id === id ? { ...rule, is_active: newActiveStatus } : rule
      )
    }));
    
    try {
      const URL = `/api/administrador/regras-laudo/${id}`;
      const token = this.props.login.user.token.access_token;
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const result = await axiosInstance.put(URL, { ativa: newActiveStatus }, { headers });
      
      if (result.status === 401) {
        this.props.history.push('/auth');
        return;
      }
      toastr.success('Sucesso', `Regra ${newActiveStatus ? 'ativada' : 'desativada'} com sucesso!`);
    } catch (error) {
      console.error('Erro ao alterar status da regra:', error);
      toastr.error('Erro', 'Erro ao alterar status da regra.');
      
      // Reverter estado em caso de erro
      this.setState(prevState => ({
        rules: prevState.rules.map(rule => 
          rule.id === id ? { ...rule, is_active: !newActiveStatus } : rule
        )
      }));
    }
  }

  handleEdit = (rule) => {
    this.props.history.push('/intranet/cadastro/customizar-pareceres/editar', { rule });
  }

  handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta regra?')) return;
    
    try {
      const URL = `/api/administrador/regras-laudo/${id}`;
      const token = this.props.login.user.token.access_token;
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const result = await axiosInstance.delete(URL, { headers });
      
      if (result.status === 401) {
        this.props.history.push('/auth');
        return;
      }
      
      this.setState(prevState => ({
        rules: prevState.rules.filter(rule => rule.id !== id)
      }));
      
      toastr.success('Sucesso', 'Regra excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir regra:', error);
      const errorMessage = error.response && error.response.data && error.response.data.message 
        ? error.response.data.message 
        : 'Erro ao excluir regra. Tente novamente.';
      toastr.error('Erro', errorMessage);
    }
  }

  handleNewRule = () => {
    const totalRulesCount = this.state.rules.length;

    AnalyticsService.capture('customization_creation_intended', {
      has_existing_rules: totalRulesCount > 0,
      total_rules_count: totalRulesCount
    });

    this.props.history.push('/intranet/cadastro/customizar-pareceres/nova');
  }

  getConditionsSummary = (conditions) => {
    if (!conditions || conditions.length === 0) return 'Nenhum critério';
    
    const fieldNames = conditions
      .slice(0, 2)
      .map(c => {
        const field = MOCK_METADATA.fields.find(f => f.id === c.field);
        return field ? field.label : c.field;
      })
      .filter(Boolean);
    
    const remaining = conditions.length - fieldNames.length;
    const summary = fieldNames.join(', ');
    
    return remaining > 0 ? `${summary} +${remaining} outro(s)` : summary;
  }

  render() {
    const { rules, draggedIndex, loading } = this.state;

    if (loading) {
      return (
        <Main>
          <Header
            title="Regras de Aprovação"
            breadcrumb={BREADCRUMB_CADASTRO}
            actualLink="Regras de Aprovação"
          />
          <MainContainer>
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ fontSize: '16px', color: '#666' }}>Carregando regras...</p>
            </div>
          </MainContainer>
        </Main>
      );
    }

    return (
      <Main>
        <Header
          title="Regras de Aprovação"
          breadcrumb={BREADCRUMB_CADASTRO}
          actualLink="Regras de Aprovação"
        />
        <MainContainer>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '8px' }}>
              Regras de Aprovação
            </h1>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Defina a política de crédito da sua imobiliária. As regras são avaliadas em <br />
              ordem de prioridade. — a primeira que der match é aplicada. <br />
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Arraste para reordenar por prioridade.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
            <button
              onClick={this.handleNewRule}
              style={{
                padding: '12px 24px',
                backgroundColor: '#0066cc',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#0052a3'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0066cc'}
            >
              + Nova Regra
            </button>
          </div>

        {rules.length === 0 ? (
          <div style={{ 
            padding: '60px', 
            textAlign: 'center', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '8px',
            border: '2px dashed #ddd'
          }}>
            <p style={{ color: '#999', fontSize: '16px', marginBottom: '16px' }}>
              Nenhuma regra cadastrada
            </p>
            <button
              onClick={this.handleNewRule}
              style={{
                padding: '10px 20px',
                backgroundColor: '#0066cc',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Criar primeira regra
            </button>
          </div>
        ) : (
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {/* Header da tabela */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '60px 110px 280px 1fr 120px 200px',
              gap: '0',
              padding: '16px 20px 16px 0',
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              fontWeight: '600',
              fontSize: '14px',
              color: '#333'
            }}>
              <div></div>
              <div>PRIORIDADE</div>
              <div>NOME DA REGRA</div>
              <div>CRITÉRIOS</div>
              <div>STATUS</div>
              <div>AÇÕES</div>
            </div>

            {/* Linhas da tabela */}
            {rules.map((rule, index) => (
              <div
                key={rule.id}
                draggable
                onDragStart={() => this.handleDragStart(index)}
                onDragOver={(e) => this.handleDragOver(e, index)}
                onDragEnd={this.handleDragEnd}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 110px 280px 1fr 120px 200px',
                  gap: '0',
                  padding: '16px 20px 16px 0',
                  alignItems: 'center',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: draggedIndex === index ? '#f0f0f0' : '#fff',
                  cursor: 'move',
                  opacity: draggedIndex === index ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  ':hover': {
                    backgroundColor: '#f9f9f9'
                  }
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = draggedIndex === index ? '#f0f0f0' : '#f9f9f9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = draggedIndex === index ? '#f0f0f0' : '#fff'}
              >
                {/* Coluna 1: Drag Handle */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="#999" style={{ cursor: 'grab' }}>
                    <circle cx="7" cy="5" r="1.5"/>
                    <circle cx="13" cy="5" r="1.5"/>
                    <circle cx="7" cy="10" r="1.5"/>
                    <circle cx="13" cy="10" r="1.5"/>
                    <circle cx="7" cy="15" r="1.5"/>
                    <circle cx="13" cy="15" r="1.5"/>
                  </svg>
                </div>

                {/* Coluna 2: Prioridade */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '900',
                  color: '#0066cc',
                  marginLeft: '22px'
                }}>
                  {index + 1}
                </div>

                {/* Coluna 3: Nome da Regra */}
                <div style={{ fontSize: '14px', color: '#333', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {rule.name}
                </div>

                {/* Coluna 4: Critérios */}
                <div style={{ fontSize: '13px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {this.getConditionsSummary(rule.conditions)}
                </div>

                {/* Coluna 5: Status */}
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: rule.is_active ? '#28a745' : '#ccc'
                  }}/>
                  <span style={{ fontSize: '13px', color: '#333', fontWeight: '500' }}>
                    {rule.is_active ? 'Ativa' : 'Rascunho'}
                  </span>
                </div>

                {/* Coluna 6: Ações */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-start' }}>
                  <button
                    onClick={() => this.handleToggleActive(rule.id)}
                    title={rule.is_active ? 'Desativar' : 'Ativar'}
                    style={{
                      position: 'relative',
                      width: '50px',
                      height: '26px',
                      backgroundColor: rule.is_active ? '#28a745' : '#ccc',
                      border: 'none',
                      borderRadius: '13px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      padding: 0,
                      outline: 'none'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '2px',
                        left: rule.is_active ? '26px' : '2px',
                        width: '22px',
                        height: '22px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    />
                  </button>

                  <button
                    onClick={() => this.handleEdit(rule)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#ffc107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#e0a800'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#ffc107'}
                  >
                    Editar
                  </button>

                  <button
                    onClick={() => this.handleDelete(rule.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                  >
                    Excluir
                  </button>
                </div>
              </div>
            ))}

            {/* Linha do Fallback */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 110px 280px 1fr 120px 200px',
                gap: '0',
                padding: '16px 20px 16px 0',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fffacd',
                cursor: 'default'
              }}
            >
              <div></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b8860b" strokeWidth="2" style={{ width: '18px', marginLeft: '28px' }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div style={{ fontWeight: '600', color: '#b8860b' }}>Parecer Padrão FC Análise</div>
              <div style={{ fontSize: '13px', color: '#8b6914' }}>Se nenhuma das regras acima atender os critérios, o sistema aplicará a regra padrão da plataforma.</div>
              <div></div>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'flex-start'
              }}>
                <div style={{
                  padding: '4px 8px',
                  backgroundColor: '#ffa500',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  Automático
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ 
          marginTop: '24px', 
          marginBottom: '60px',
          padding: '16px', 
          backgroundColor: '#f0f8ff', 
          border: '1px solid #0066cc',
          borderRadius: '4px'
        }}>
          <p style={{ fontSize: '13px', color: '#0066cc', margin: 0 }}>
            <strong>💡 Dica:</strong> A ordem das regras define a prioridade de execução. 
            A primeira regra que atender todos os critérios será aplicada.
          </p>
        </div>
        </MainContainer>
      </Main>
    );
  }
}

const mapStateToProps = (state) => ({
  login: state.login,
});

export default connect(mapStateToProps)(withRouter(RuleList));
